import { createContext, useContext, useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { demoApi } from '../lib/api/demo';
import { now, captureSince } from '../lib/perfTiming';

const KaraokeContext = createContext(null);

const TAMIL_GRAPHEME_SEGMENTER = new Intl.Segmenter('ta', { granularity: 'grapheme' });

const WARMUP = 0.3;
// 'pulse' = fade in then back out around each char's midpoint
// 'cumulative' = fade in once, then stay lit
const KARAOKE_MODE = 'cumulative';

function computeIntensity(t, start, end) {
  const mid = (start + end) / 2;
  if (KARAOKE_MODE === 'cumulative') {
    if (t >= mid) return 1;
    const d = mid - t;
    if (d > WARMUP) return 0;
    return 1 - d / WARMUP;
  }
  const d = Math.abs(t - mid);
  if (d > WARMUP) return 0;
  return Math.max(0, 1 - d / WARMUP);
}

/**
 * Build a cache entry ({ audio_url, chars }) from an ElevenLabs
 * normalized_alignment payload. Shared by live loads and prefetch priming.
 * Returns null if the alignment is missing/malformed.
 */
function entryFromAlignment(normalized, audio_url) {
  if (!normalized?.characters || !audio_url) return null;
  const chars = normalized.characters.map((c, i) => ({
    ch: c,
    start: normalized.character_start_times_seconds[i],
    end: normalized.character_end_times_seconds[i],
  }));
  return { audio_url, chars };
}

// Cache key is trimmed so a prefetched sentence (trimmed) matches the click's
// `fullSentence` (which can carry a leading space). The server trims before
// hashing too, so this aligns client cache, server cache, and pregen.
const cacheKey = (text) => (text || '').trim();

/**
 * Inline karaoke: drives per-character intensity for whichever sentence/word
 * is currently playing. Story components call useKaraokeIntensities(text) to
 * subscribe; the matching text renders char-by-char opacity while audio plays.
 */
export function KaraokeProvider({ children }) {
  const audioRef = useRef(null);
  const [active, setActive] = useState(null); // { text, chars }
  const [loading, setLoading] = useState(null); // text being loaded
  const listenersRef = useRef(new Set());
  const rafRef = useRef(null);
  const cacheRef = useRef(new Map()); // text -> { audio_url, chars }

  // Ensure a single shared Audio element
  useEffect(() => {
    audioRef.current = new Audio();
    audioRef.current.crossOrigin = 'anonymous';
    return () => { audioRef.current?.pause(); };
  }, []);

  const emit = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const t = audio.currentTime;
    listenersRef.current.forEach((fn) => fn(t));
  }, []);

  const startLoop = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || audio.paused || audio.ended) return;
    emit();
    rafRef.current = requestAnimationFrame(startLoop);
  }, [emit]);

  const loadKaraoke = useCallback(async (resourceType, text) => {
    const key = cacheKey(text);
    if (cacheRef.current.has(key)) return cacheRef.current.get(key);
    const res = await demoApi.getKaraoke(resourceType, text);
    if (!res?.success) throw new Error('No karaoke data');
    const entry = entryFromAlignment(res.alignment?.normalized_alignment, res.audio_url);
    if (!entry) throw new Error('No alignment');
    cacheRef.current.set(key, entry);
    return entry;
  }, []);

  /**
   * Seed the cache with already-generated clips (from the prefetch endpoint) so
   * the first click plays without an origin round-trip. Does not fetch audio —
   * the mp3 is warmed separately by the caller.
   */
  const primeCache = useCallback((entries) => {
    if (!Array.isArray(entries)) return;
    for (const e of entries) {
      const key = cacheKey(e?.text);
      if (!key || cacheRef.current.has(key)) continue;
      const entry = entryFromAlignment(e.normalized_alignment, e.audio_url);
      if (entry) cacheRef.current.set(key, entry);
    }
  }, []);

  // Drop a cached entry so the next play re-fetches it. Used after an admin
  // regenerates a clip: the on-disk file changed, but this client cache would
  // otherwise keep replaying the stale alignment/url.
  const invalidate = useCallback((text) => {
    cacheRef.current.delete(cacheKey(text));
  }, []);

  const playKaraoke = useCallback(async (resourceType, text) => {
    if (!text) return;
    const audio = audioRef.current;
    if (!audio) return;
    // Time from intent (this call, fired synchronously off the click) to the
    // moment audio actually starts. `cached` splits already-loaded clips from
    // ones that pay a karaoke fetch.
    const t0 = now();
    const cached = cacheRef.current.has(cacheKey(text));
    audio.pause();
    setLoading(text);
    try {
      const entry = await loadKaraoke(resourceType, text);
      setActive({ text, chars: entry.chars });
      if (audio.src !== new URL(entry.audio_url, window.location.href).href) {
        audio.src = entry.audio_url;
      }
      audio.currentTime = 0;
      await audio.play();
      captureSince(t0, 'demo_audio_timing', { resourceType, cached, textLength: text.length });
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(startLoop);
    } catch (e) {
      console.error('[karaoke] play failed', e);
      setActive(null);
    } finally {
      setLoading(null);
    }
  }, [loadKaraoke, startLoop]);

  /**
   * Play slices of a parent clip (e.g. subsections of a sentence) back-to-back.
   * ranges = [{ startChar, endChar }] in character indices of `parentText`.
   * Uses the parent's existing audio file — no new ElevenLabs calls.
   */
  const playKaraokeSlices = useCallback(async (resourceType, parentText, ranges, gapMs = 200) => {
    if (!ranges?.length) return;
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    setLoading(parentText);
    let entry;
    try {
      entry = await loadKaraoke(resourceType, parentText);
    } catch (e) {
      console.error('[karaoke] slice load failed', e);
      setLoading(null);
      return;
    }
    setLoading(null);
    setActive({ text: parentText, chars: entry.chars, slices: ranges });
    if (audio.src !== new URL(entry.audio_url, window.location.href).href) {
      audio.src = entry.audio_url;
    }

    for (const { startChar, endChar } of ranges) {
      const startCh = entry.chars[startChar];
      const endCh = entry.chars[endChar - 1];
      if (!startCh || !endCh) continue;
      const startTime = startCh.start;
      const endTime = endCh.end;
      audio.currentTime = startTime;
      try { await audio.play(); } catch (_) { break; }
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(startLoop);
      await new Promise((resolve) => {
        const check = () => {
          if (audio.paused || audio.ended || audio.currentTime >= endTime) {
            audio.pause();
            resolve();
          } else {
            requestAnimationFrame(check);
          }
        };
        check();
      });
      if (gapMs > 0) await new Promise((r) => setTimeout(r, gapMs));
    }
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    // Hold the final per-char state: in-slice chars at intensity 1 (cumulative),
    // out-of-slice at floor 0.2. Plain-text rendering after teardown matches
    // those opacities, so clearing active produces no visual change.
    setActive(null);
  }, [loadKaraoke, startLoop]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onEnd = () => {
      setActive(null);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      // Emit one final zero-intensity tick
      listenersRef.current.forEach((fn) => fn(Infinity));
    };
    const onPause = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    audio.addEventListener('ended', onEnd);
    audio.addEventListener('pause', onPause);
    return () => {
      audio.removeEventListener('ended', onEnd);
      audio.removeEventListener('pause', onPause);
    };
  }, []);

  const subscribe = useCallback((fn) => {
    listenersRef.current.add(fn);
    return () => listenersRef.current.delete(fn);
  }, []);

  // Play a list of phrases sequentially, each with its own ElevenLabs
  // timestamped clip, inserting `gapMs` between them.
  const playKaraokeSequence = useCallback(async (resourceType, phrases, gapMs = 200) => {
    const audio = audioRef.current;
    if (!audio) return;
    for (const text of phrases) {
      if (!text) continue;
      const done = new Promise((resolve) => {
        const onEnd = () => { audio.removeEventListener('ended', onEnd); resolve(); };
        audio.addEventListener('ended', onEnd);
      });
      await playKaraoke(resourceType, text);
      await done;
      if (gapMs > 0) await new Promise((r) => setTimeout(r, gapMs));
    }
  }, [playKaraoke]);

  const value = useMemo(() => ({
    playKaraoke,
    playKaraokeSequence,
    playKaraokeSlices,
    primeCache,
    invalidate,
    active,
    loading,
    subscribe,
  }), [playKaraoke, playKaraokeSequence, playKaraokeSlices, primeCache, invalidate, active, loading, subscribe]);

  return (
    <KaraokeContext.Provider value={value}>
      {children}
    </KaraokeContext.Provider>
  );
}

export function useKaraoke() {
  const ctx = useContext(KaraokeContext);
  if (!ctx) throw new Error('useKaraoke must be used within KaraokeProvider');
  return ctx;
}

/**
 * Safe accessor for the cache-invalidator that works outside a KaraokeProvider
 * (e.g. the landing page renders no provider). Returns a no-op when absent.
 */
export function useKaraokeInvalidate() {
  const ctx = useContext(KaraokeContext);
  return ctx?.invalidate ?? (() => {});
}

/**
 * Returns per-character intensities (0..1) for `text` when it's the active
 * karaoke target; otherwise null. Array length === text character count from
 * the alignment (matches ElevenLabs normalized chars, which track the input
 * string 1:1 for Tamil).
 */
export function useKaraokeIntensities(text) {
  const ctx = useContext(KaraokeContext);
  const [intensities, setIntensities] = useState(null);
  const isActive = ctx?.active?.text === text && text != null;

  useEffect(() => {
    if (!ctx) return;
    if (!isActive) { setIntensities(null); return; }
    const chars = ctx.active.chars;
    const unsub = ctx.subscribe((t) => {
      setIntensities(chars.map(({ start, end }) => computeIntensity(t, start, end)));
    });
    return unsub;
  }, [ctx, isActive]);

  return isActive ? intensities : null;
}

/**
 * Renders `text` as a sequence of spans with per-char opacity from `intensities`.
 * Used by both StoryContent and any AudioText that wants to show karaoke locally.
 */
export function KaraokeText({ text, intensities, floor = 0 }) {
  const dim = 0.15;
  const graphemes = [...TAMIL_GRAPHEME_SEGMENTER.segment(text)];
  let cpIndex = 0;
  return graphemes.map((seg, gi) => {
    const codePoints = Array.from(seg.segment);
    let maxIntensity = 0;
    for (let j = 0; j < codePoints.length; j++) {
      maxIntensity = Math.max(maxIntensity, intensities?.[cpIndex + j] || 0);
    }
    cpIndex += codePoints.length;
    const karaokeOpacity = dim + (1 - dim) * maxIntensity;
    const opacity = Math.max(karaokeOpacity, floor);
    return (
      <span
        key={gi}
        style={{
          opacity,
          transition: 'opacity 200ms ease-out',
        }}
      >
        {seg.segment}
      </span>
    );
  });
}

export function useKaraokeLoading(text) {
  const ctx = useContext(KaraokeContext);
  return ctx?.loading === text;
}

/**
 * Single top-level subscription: returns { activeText, intensities } where
 * intensities is a number[] per-character (aligned to activeText chars) or
 * null when nothing is playing. Meant to be called ONCE in a parent; consumers
 * downstream check activeText to decide whether their text is being spoken.
 */
export function useKaraokeState() {
  const ctx = useContext(KaraokeContext);
  const [intensities, setIntensities] = useState(null);
  const activeText = ctx?.active?.text ?? null;
  const slices = ctx?.active?.slices ?? null;

  useEffect(() => {
    if (!ctx || !ctx.active) { setIntensities(null); return; }
    const chars = ctx.active.chars;
    const activeSlices = ctx.active.slices;
    const inSlice = activeSlices && activeSlices.length > 0
      ? (i) => activeSlices.some(({ startChar, endChar }) => i >= startChar && i < endChar)
      : null;
    setIntensities(new Array(chars.length).fill(0));
    const unsub = ctx.subscribe((t) => {
      // Sentinel: emit full-brightness for all chars (bypasses slice mask).
      // Used at end of subsection playback to fade out-of-slice dim smoothly
      // before teardown — avoids a flash when plain text takes over.
      if (t === Infinity) {
        setIntensities(new Array(chars.length).fill(1));
        return;
      }
      setIntensities(chars.map(({ start, end }, i) => {
        // When playing subsections, chars outside any slice never light up —
        // otherwise cumulative mode would mark skipped chars as "spoken".
        if (inSlice && !inSlice(i)) return 0;
        return computeIntensity(t, start, end);
      }));
    });
    return unsub;
  }, [ctx, activeText]);

  return { activeText, intensities, slices };
}
